// The econ calendar view. (World clocks live in clock.ts.) Markets used to be
// its own page with Clock/Calendar tabs; both now sit as their own sections on
// the Dashboard, so this module just exports the calendar content renderer.

import { el } from "../lib/dom.js";
import { upcomingEconEvents } from "../lib/econ-calendar.js";
import { formatDuration } from "../lib/session-math.js";

const etTimeLabel = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export function renderCalendar(root: HTMLElement): () => void {
  const container = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", gap: "8px", height: "100%" } });
  root.replaceChildren(container);

  function render() {
    const now = Date.now();
    container.replaceChildren();
    const events = upcomingEconEvents(20).filter((e) => e.date.getTime() > now - 3600_000);
    if (!events.length) {
      // Empty state, vertically centred to match the other Monitor sections.
      container.appendChild(
        el("div", { style: { flex: "1", minHeight: "0", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 24px" } }, [
          el("div", { text: "No scheduled events on the calendar horizon.", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
        ])
      );
      return;
    }
    const [next, ...rest] = events;
    const minutes = (next!.date.getTime() - now) / 60000;
    const countdown =
      minutes > 48 * 60 ? `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h` : formatDuration(minutes);
    container.appendChild(
      el("div", {
        style: {
          background: "var(--bg-fill)",
          borderLeft: "3px solid var(--accent-orange)",
          borderRadius: "0 10px 10px 0",
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: "3px",
        },
      }, [
        el("div", { text: "Next event", style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.08em", textTransform: "uppercase" } }),
        el("div", { text: `${next!.name} — in ${countdown}`, style: { font: "var(--font-title)" } }),
        el("div", { text: `${etTimeLabel.format(next!.date)} ET`, style: { font: "var(--font-caption)", color: "var(--text-secondary)" } }),
      ])
    );
    // Remaining events in a titled, elevated card (matches System overview / clocks).
    if (rest.length) {
      const rows = rest.map((event) =>
        el("div", { style: { display: "flex", alignItems: "center", gap: "8px", minHeight: "26px" } }, [
          el("div", { text: event.name, style: { font: "var(--font-body)", flex: "1" } }),
          el("div", {
            text: `${etTimeLabel.format(event.date)} ET`,
            style: { font: "var(--font-caption)", color: "var(--text-secondary)", fontVariantNumeric: "tabular-nums", flex: "none" },
          }),
        ])
      );
      container.appendChild(
        el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } }, [
          el("div", { text: "Upcoming", style: { font: "var(--font-label)", color: "var(--text-secondary)", letterSpacing: "0.06em", textTransform: "uppercase", padding: "0 2px" } }),
          el("div", { style: { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "12px", padding: "6px 12px", display: "flex", flexDirection: "column", gap: "2px" } }, rows),
        ])
      );
    }
  }
  render();
  const interval = setInterval(render, 30_000);
  return () => clearInterval(interval);
}
